/* Per-customer Deal Jacket view — folders + templates + generated history. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";

export default function DealJacketTab({ customerId, onJumpToActivity }) {
  const [data, setData] = useState(null);
  const [activeFolder, setActiveFolder] = useState("__all__");
  const [packet, setPacket] = useState({});  // {template_id: true}
  const [busy, setBusy] = useState(false);
  const [quickPacks, setQuickPacks] = useState([]);
  const [packBusy, setPackBusy] = useState("");
  const [packMsg, setPackMsg] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [dealType, setDealType] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [dj, qp, init] = await Promise.all([
      api.get(`/admin/crm/customer/${customerId}/deal-jacket`),
      api.get("/admin/documents/quick-packs", { params: { active_only: true } }).catch(() => ({ data: { items: [] } })),
      api.get(`/admin/crm/customer/${customerId}/deal-jacket/readiness`,
              { params: dealType ? { deal_type: dealType } : {} }).catch(() => null),
    ]);
    setData(dj.data);
    setQuickPacks((qp.data?.items || []).filter(p => p.active));
    if (init?.data) {
      setReadiness(init.data);
      if (!dealType && init.data.deal_type) setDealType(init.data.deal_type);
    }
  }, [customerId, dealType]);
  useEffect(() => { load(); }, [load]);

  const folders = data?.folders || [];
  const templates = useMemo(() => {
    if (!data) return [];
    if (activeFolder === "__all__") return data.templates || [];
    return (data.templates || []).filter(t => t.folder === activeFolder);
  }, [data, activeFolder]);

  const folderCounts = useMemo(() => {
    const out = { __all__: (data?.templates || []).length };
    (data?.templates || []).forEach(t => { out[t.folder] = (out[t.folder] || 0) + 1; });
    return out;
  }, [data]);

  const openForm = async (template, save = false) => {
    setBusy(true);
    try {
      // PDF templates → open in the true PDF viewer (pdf-lib + pdfjs).
      // Image (rasterized) templates → keep the legacy overlay editor.
      if (template.file_kind === "pdf") {
        const params = new URLSearchParams({ template_id: template.id }).toString();
        navigate(`/admin/crm/customer/${customerId}/deal-jacket/pdf-form?${params}`);
        return;
      }
      const r = await api.post(`/admin/crm/customer/${customerId}/deal-jacket/generate`,
        { template_id: template.id, save });
      const params = new URLSearchParams({
        template_id: template.id,
        ...(r.data.generated_id ? { generated_id: r.data.generated_id } : {}),
      }).toString();
      navigate(`/admin/crm/customer/${customerId}/deal-jacket/form?${params}`);
    } catch (e) {
      alert("Failed to open form: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const openGenerated = (g) => {
    // If this was saved as a real filled PDF (from the pdf-lib viewer), reopen
    // the PDF viewer; otherwise fall back to the legacy overlay editor.
    if (g.filled_pdf_doc_id) {
      const params = new URLSearchParams({ template_id: g.template_id, generated_id: g.id, pdf: "1" }).toString();
      navigate(`/admin/crm/customer/${customerId}/deal-jacket/pdf-form?${params}`);
      return;
    }
    const params = new URLSearchParams({ template_id: g.template_id, generated_id: g.id }).toString();
    navigate(`/admin/crm/customer/${customerId}/deal-jacket/form?${params}`);
  };

  const deleteGenerated = async (g) => {
    if (!window.confirm(`Delete this saved "${g.template_name}"?`)) return;
    await api.delete(`/admin/crm/customer/${customerId}/deal-jacket/generated/${g.id}`);
    load();
  };

  const togglePacket = (tid) => setPacket(p => ({ ...p, [tid]: !p[tid] }));
  const packetCount = Object.values(packet).filter(Boolean).length;
  const printPacket = async () => {
    const ids = Object.entries(packet).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/crm/customer/${customerId}/deal-jacket/packet`,
        { template_ids: ids, save: false });
      // Sequentially open each — most browsers limit to one, so open the first and let user click next.
      // Better UX: open a packet-print view that stacks them. For MVP we open them in tabs.
      r.data.items.forEach(item => {
        const params = new URLSearchParams({ template_id: item.template.id, packet: "1" }).toString();
        window.open(`/admin/crm/customer/${customerId}/deal-jacket/form?${params}`, "_blank");
      });
      setPacket({});
    } finally { setBusy(false); }
  };

  const runQuickPack = async (pk) => {
    setPackBusy(pk.id); setPackMsg("");
    try {
      const r = await api.post(`/admin/crm/customer/${customerId}/quick-pack/${pk.id}/generate?save=true`);
      // Prefer the signed-link URL (no base64 bloat); fall back to file_b64.
      if (r.data?.view_url) {
        const u = `${api.defaults.baseURL || ""}${r.data.view_url}`;
        window.open(u, "_blank");
      } else if (r.data?.file_b64) {
        const bin = atob(r.data.file_b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        const blob = new Blob([arr], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      const warn = (r.data?.fill_warnings || []).length;
      setPackMsg(`✓ "${pk.name}" generated · ${r.data?.templates_included?.length || 0} forms` +
                  (warn ? ` · ${warn} warning${warn === 1 ? '' : 's'}` : '') +
                  ` · saved to Deal Jacket.`);
      load();
    } catch (e) {
      setPackMsg("Quick Pack failed: " + (e?.response?.data?.detail || e.message));
    } finally { setPackBusy(""); }
  };

  if (!data) return <div className="text-gray-400 p-4" data-testid="dj-loading">Loading Deal Jacket…</div>;
  const c = data.customer || {};

  return (
    <div data-testid="dj-tab">
      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3" data-testid="dj-summary">
        <Summary label="Customer" value={c.name} sub={c.phone || c.email} />
        <Summary label="Purchase" value={fmtV(c.purchase_vehicle)} sub={c.purchase_vehicle?.vin} />
        <Summary label="Vehicle of Interest" value={fmtV(c.vehicle_of_interest)} sub={c.vehicle_of_interest?.vin} />
        <Summary label="Trade-In" value={fmtV(c.trade_vehicle)} sub={c.trade_vehicle?.vin} />
      </div>
      {data.missing_items?.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded p-2 mb-3 text-sm" data-testid="dj-missing">
          <span className="font-semibold text-amber-800">Still needed: </span>{data.missing_items.join(" · ")}
        </div>
      )}

      {/* ----- Round-24: readiness + per-deal-type checklist ----- */}
      {readiness && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 shadow-sm" data-testid="dj-readiness">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Deal type</span>
              <select value={dealType} onChange={e => setDealType(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 capitalize"
                data-testid="dj-deal-type">
                {(readiness.available_deal_types || []).map(dt => (
                  <option key={dt} value={dt}>{dt}</option>
                ))}
              </select>
            </div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
              readiness.ready ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
            }`} data-testid="dj-ready-badge">
              {readiness.ready ? "✓ Ready to Finalize" : `✕ ${readiness.missing_count} field${readiness.missing_count === 1 ? "" : "s"} missing`}
            </span>
            <span className="text-[10px] text-gray-500">
              Documents: <b className={readiness.checklist_required_completed === readiness.checklist_required_total && readiness.checklist_required_total > 0 ? "text-emerald-700" : "text-amber-700"}>{readiness.checklist_required_completed}/{readiness.checklist_required_total}</b> required completed
            </span>
          </div>
          {!readiness.ready && (
            <details className="text-xs" open data-testid="dj-missing-fields">
              <summary className="cursor-pointer font-bold text-red-700 mb-1">{readiness.missing_count} required field{readiness.missing_count === 1 ? "" : "s"} to fill before Final</summary>
              <ul className="space-y-1 mt-1">
                {readiness.missing.map((m, i) => (
                  <li key={i} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-2 py-1">
                    <span className="text-red-600">✕</span>
                    <div className="flex-1">
                      <div className="font-semibold text-red-900">{m.label}</div>
                      {m.hint && <div className="text-[10px] text-red-700/70">{m.hint}</div>}
                    </div>
                    <button onClick={() => onJumpToActivity?.(m.tab)}
                      className="text-[10px] bg-red-600 hover:bg-red-500 text-white font-bold px-2 py-1 rounded whitespace-nowrap"
                      data-testid={`dj-jump-${m.tab}`}>
                      Open {m.tab.replace("_", " ")} →
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <details className="text-xs mt-2" data-testid="dj-checklist">
            <summary className="cursor-pointer font-bold text-[#061426] mb-1">Required documents for {readiness.deal_type} deals ({readiness.checklist_required_completed}/{readiness.checklist_required_total})</summary>
            <ul className="space-y-0.5 mt-1">
              {readiness.checklist.map((row, i) => (
                <li key={i} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50">
                  <span className={row.completed ? "text-emerald-600" : row.required ? "text-amber-600" : "text-gray-400"}>
                    {row.completed ? "✓" : row.required ? "•" : "○"}
                  </span>
                  <span className={`flex-1 ${row.completed ? "text-gray-500 line-through" : "text-[#061426]"}`}>
                    {row.label}
                    {!row.required && <span className="text-[10px] text-gray-400 ml-1">(optional)</span>}
                  </span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                    row.status === "Final" ? "bg-emerald-100 text-emerald-700" :
                    row.status === "Signed" ? "bg-blue-100 text-blue-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>{row.status}</span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* Quick Packs — admin-built form playlists */}
      {quickPacks.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200 rounded p-3 mb-3" data-testid="dj-quick-packs">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <span className="text-[10px] uppercase tracking-wide text-blue-700 font-bold">Quick Packs</span>
              <span className="ml-2 text-xs text-gray-500">One click → autofills + merges + saves to jacket</span>
            </div>
            <a href="/admin/documents/quick-packs" className="text-[10px] text-blue-600 hover:underline" target="_blank" rel="noreferrer">Manage</a>
          </div>
          <div className="flex flex-wrap gap-2">
            {quickPacks.map(pk => (
              <button key={pk.id} type="button" onClick={() => runQuickPack(pk)}
                disabled={packBusy === pk.id || busy}
                className="text-xs bg-white border-2 border-blue-300 hover:border-blue-500 text-[#061426] rounded px-3 py-1.5 font-bold disabled:opacity-60 disabled:cursor-wait"
                title={pk.description || ""}
                data-testid={`dj-quick-pack-${pk.id}`}>
                {packBusy === pk.id ? "Generating…" : <>📚 {pk.name}<span className="ml-1 text-gray-500 font-normal">· {(pk.items || []).length} forms</span></>}
              </button>
            ))}
          </div>
          {packMsg && <div className="text-[11px] text-gray-700 mt-2" data-testid="dj-quick-pack-msg">{packMsg}</div>}
        </div>
      )}

      {/* Folder chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        <FolderChip name="All" count={folderCounts.__all__ || 0} active={activeFolder === "__all__"} onClick={() => setActiveFolder("__all__")} testid="dj-folder-all" />
        {folders.map(f => (
          <FolderChip key={f.name} name={f.name} count={folderCounts[f.name] || 0}
            active={activeFolder === f.name} onClick={() => setActiveFolder(f.name)}
            testid={`dj-folder-${f.name.replace(/\s+/g, '-')}`} />
        ))}
      </div>

      {/* Template cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="dj-template-grid">
        {templates.map(t => (
          <div key={t.id} className="border rounded p-3 bg-white flex flex-col" data-testid={`dj-tpl-${t.id}`}>
            <div className="flex items-start gap-2">
              <input type="checkbox" checked={!!packet[t.id]} onChange={() => togglePacket(t.id)}
                title="Add to packet" data-testid={`dj-packet-${t.id}`} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#061426] truncate">{t.name}</div>
                <div className="text-xs text-gray-500">{t.folder} · v{t.version} · {(t.fields || []).length} fields</div>
              </div>
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button className="text-xs text-blue-600 hover:underline" onClick={() => openForm(t, false)} disabled={busy} data-testid={`dj-open-${t.id}`}>Open / Fill</button>
              <button className="text-xs text-emerald-700 hover:underline" onClick={() => openForm(t, true)} disabled={busy} data-testid={`dj-save-${t.id}`}>Generate &amp; Save</button>
              <a className="text-xs text-gray-500 hover:underline ml-auto" href={`/admin/documents/templates/${t.id}/edit`} target="_blank" rel="noreferrer">Edit Template</a>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 text-center text-gray-400 p-6 border-2 border-dashed rounded">
            No active templates {activeFolder !== "__all__" ? `in "${activeFolder}"` : ""} yet.{" "}
            <a href="/admin/documents/templates" className="text-blue-600 hover:underline" data-testid="dj-upload-link">Upload one →</a>
          </div>
        )}
      </div>

      {packetCount > 0 && (
        <div className="sticky bottom-2 mt-3 bg-[#061426] text-white rounded p-3 flex items-center gap-3" data-testid="dj-packet-bar">
          <span className="font-semibold">{packetCount} form{packetCount === 1 ? "" : "s"} in packet</span>
          <button className="text-xs text-white/70 hover:underline" onClick={() => setPacket({})}>Clear</button>
          <div className="flex-1" />
          <button className="ssc-btn-primary" onClick={printPacket} disabled={busy} data-testid="dj-packet-go">Open Deal Packet</button>
        </div>
      )}

      {/* Generated history */}
      <h3 className="font-bold text-sm mt-5 mb-2">Generated Documents · History</h3>
      <div className="bg-white rounded border overflow-x-auto">
        <table className="w-full text-sm" data-testid="dj-generated">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="p-2 text-left">Document</th><th className="p-2 text-left">Version</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Updated</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {(data.generated || []).map(g => (
              <tr key={g.id} className="border-t" data-testid={`dj-gen-${g.id}`}>
                <td className="p-2 font-semibold">{g.template_name}</td>
                <td className="p-2 text-xs">v{g.template_version}</td>
                <td className="p-2 text-xs"><span className="px-2 py-0.5 bg-gray-100 rounded">{g.status}</span></td>
                <td className="p-2 text-xs text-gray-500">{(g.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-2 text-right">
                  <button className="text-xs text-blue-600 hover:underline mr-2" onClick={() => openGenerated(g)}>Open</button>
                  <button className="text-xs text-red-600 hover:underline" onClick={() => deleteGenerated(g)}>Delete</button>
                </td>
              </tr>
            ))}
            {(data.generated || []).length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-gray-400">No generated documents yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtV(v) {
  if (!v) return "—";
  return [v.year, v.make, v.model, v.trim].filter(Boolean).join(" ") || "—";
}
function FolderChip({ name, count, active, onClick, testid }) {
  return (
    <button onClick={onClick}
      className={`text-xs px-3 py-1 rounded-full border ${active ? "bg-[#006DFF] text-white border-[#006DFF]" : "bg-white text-gray-700 border-gray-300"}`}
      data-testid={testid}>{name} <span className="opacity-70">({count})</span></button>
  );
}
function Summary({ label, value, sub }) {
  return (
    <div className="border border-gray-200 rounded p-2 bg-white">
      <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-semibold truncate">{value || "—"}</div>
      {sub && <div className="text-xs text-gray-500 truncate">{sub}</div>}
    </div>
  );
}
